// src/hooks/useCostSimulatorState.ts
import { useState, useMemo, useCallback } from 'react';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import {
  generateMonthlyRollups,
  findBreakEvenMonth,
  calculateOnPremTco,
  calculateMonthlyTokenVolume,
  DEFAULT_COST_CONFIG,
  type CostSimulatorConfig,
  type MonthlyRollupEnhanced,
  type CostComparisonResult,
} from '@/lib/costCalculations';
import { aiServiceModelPresets, type AiServiceModelPreset } from '@/lib/aiServiceModels';

export interface UseCostSimulatorStateReturn {
  // Config state
  config: CostSimulatorConfig;
  setConfig: (config: Partial<CostSimulatorConfig>) => void;
  resetConfig: () => void;
  
  // AI model selection
  selectedModel: AiServiceModelPreset | undefined;
  setSelectedModel: (modelValue: string) => void;
  
  // Computed values
  serverCount: number;
  monthlyRollups: MonthlyRollupEnhanced[];
  comparison: CostComparisonResult;
  monthlyTokens: { inputTokens: number; outputTokens: number; totalTokens: number };
  onPremBreakdown: ReturnType<typeof calculateOnPremTco>;
  
  // For chart compatibility
  chartRollups: Array<{
    monthKey: string;
    cumOnPremTcoUSD: number;
    cumCloudUSD: number;
  }>;
}

export function useCostSimulatorState(
  snapshot: NewSizingSnapshot | null
): UseCostSimulatorStateReturn {
  const [config, setConfigState] = useState<CostSimulatorConfig>(DEFAULT_COST_CONFIG);

  const setConfig = useCallback((partial: Partial<CostSimulatorConfig>) => {
    setConfigState(prev => ({ ...prev, ...partial }));
  }, []);

  const resetConfig = useCallback(() => {
    setConfigState(DEFAULT_COST_CONFIG);
  }, []);

  const selectedModel = useMemo(
    () => aiServiceModelPresets.find(m => m.value === config.aiServiceModel),
    [config.aiServiceModel]
  );

  const setSelectedModel = useCallback((modelValue: string) => {
    const preset = aiServiceModelPresets.find(m => m.value === modelValue);
    if (preset) {
      setConfigState(prev => ({
        ...prev,
        aiServiceModel: modelValue,
        inputCostPerMToken: preset.inputCost ?? prev.inputCostPerMToken,
        outputCostPerMToken: preset.outputCost ?? prev.outputCostPerMToken,
      }));
    } else {
      setConfigState(prev => ({ ...prev, aiServiceModel: modelValue }));
    }
  }, []);

  const serverCount = useMemo(() => {
    const count = snapshot ? Math.ceil(snapshot.finalGpuCount / config.gpusPerServer) : 0;
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '[CostSim] serverCount',
        `gpus=${snapshot?.finalGpuCount ?? 0}`,
        `gps=${config.gpusPerServer}`,
        `servers=${count}`
      );
    }
    return count;
  }, [snapshot, config.gpusPerServer]);

  const monthlyRollups = useMemo(() => {
    const rollups = generateMonthlyRollups(snapshot, config, 60);
    if (process.env.NODE_ENV !== 'production') {
      const first = rollups[0];
      const last = rollups[rollups.length - 1];
      console.log(
        '[CostSim] monthlyRollups',
        `hasSnap=${Boolean(snapshot)}`,
        `gpus=${snapshot?.finalGpuCount ?? 0}`,
        `promptTps=${snapshot?.avgPromptTokensPerSec ?? 0}`,
        `decodeTps=${snapshot?.avgDecodeTokensPerSec ?? 0}`,
        `count=${rollups.length}`,
        `first=${first ? `${first.monthKey},cloud=${Math.round(first.cumCloudUSD)},onprem=${Math.round(first.cumOnPremTcoUSD)}` : 'none'}`,
        `last=${last ? `${last.monthKey},cloud=${Math.round(last.cumCloudUSD)},onprem=${Math.round(last.cumOnPremTcoUSD)}` : 'none'}`
      );
    }
    return rollups;
  }, [snapshot, config]);

  const comparison = useMemo(
    () => findBreakEvenMonth(monthlyRollups),
    [monthlyRollups]
  );

  const monthlyTokens = useMemo(
    () => calculateMonthlyTokenVolume(snapshot, config, 0),
    [snapshot, config]
  );

  const onPremBreakdown = useMemo(
    () => calculateOnPremTco(serverCount, config),
    [serverCount, config]
  );

  // Transform for EnhancedChart compatibility
  const chartRollups = useMemo(() => {
    const rows = monthlyRollups.map(r => ({
      monthKey: r.monthKey,
      cumOnPremTcoUSD: r.cumOnPremTcoUSD,
      cumCloudUSD: r.cumCloudUSD,
    }));
    if (process.env.NODE_ENV !== 'production') {
      const first = rows[0];
      const last = rows[rows.length - 1];
      console.log(
        '[CostSim] chartRollups',
        `count=${rows.length}`,
        `first=${first ? `${first.monthKey},cloud=${Math.round(first.cumCloudUSD)},onprem=${Math.round(first.cumOnPremTcoUSD)}` : 'none'}`,
        `last=${last ? `${last.monthKey},cloud=${Math.round(last.cumCloudUSD)},onprem=${Math.round(last.cumOnPremTcoUSD)}` : 'none'}`
      );
    }
    return rows;
  }, [monthlyRollups]);

  return {
    config,
    setConfig,
    resetConfig,
    selectedModel,
    setSelectedModel,
    serverCount,
    monthlyRollups,
    comparison,
    monthlyTokens,
    onPremBreakdown,
    chartRollups,
  };
}
